import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { RolePicker } from '@/components/forms/RolePicker';
import { normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';
import { hasEmoji } from '@/shared/utils/emoji';
import { focusFirstInvalidFieldAfterRender } from '@/shared/utils/focus-first-invalid-field';

import { useAssignableRoles } from '../hooks/use-assignable-roles';
import {
  useResendInvitation,
  useUpdateAdmin,
} from '../hooks/use-admin-mutations';
import { useAdminEditStore } from '../store/admin-edit.store';
import {
  AdminApiError,
  type Admin,
  type AdminStatus,
} from '../types/admin.types';
import { getAdminErrorMessage } from '../utils/error-message';
import { formatLastLogin } from '../utils/format-date';
import { AdminResendPreviewModal } from './AdminResendPreviewModal';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

// PRD 15 §4.2: required, exactly 11 digits, '010'-prefixed (tested on digits-only input).
const PHONE_REGEX = /^010\d{8}$/;

// Send-time chrome string for the resend preview card meta (YYYY-MM-DD HH:mm),
// captured once when the preview opens so it doesn't tick while the modal is up.
function formatSentAt(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

interface EditFormState {
  name: string;
  phone: string;
  roles: string[];
  status: AdminStatus;
  note: string;
}

type FieldKey = 'name' | 'phone' | 'roles' | 'status' | 'note';

// Pending discard intent — drives the shared ConfirmModal between Close and Withdraw flows.
type PendingDiscardAction = 'close' | 'withdraw' | null;

const EMPTY_FORM: EditFormState = {
  name: '',
  phone: '',
  roles: [],
  status: 'ACTIVE',
  note: '',
};

export function AdminEditPanel() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { canWrite, denyProps, tooltip } = useWriteGuard('SYSTEM_MGMT');
  const assignableRoles = useAssignableRoles();
  const updateMutation = useUpdateAdmin();
  const resendMutation = useResendInvitation();
  const currentAdminId = useAuthStore((s) => s.currentAdminId);

  const editing = useAdminEditStore((s) => s.editing);
  const closeStore = useAdminEditStore((s) => s.close);

  const [form, setForm] = useState<EditFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscardAction>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [resendPreviewOpen, setResendPreviewOpen] = useState(false);
  const [resendSentAt, setResendSentAt] = useState('');
  // SHCT-694 — scope for the "focus the first field that failed" sweep.
  const formRef = useRef<HTMLDivElement>(null);

  const isSelfRow = editing != null && editing.id === currentAdminId;
  const isWithdrawnRow = editing?.status === 'WITHDRAWN';
  const isUnregisteredRow = editing?.status === 'PENDING';
  // Locking the status select when row state forbids transitions (PRD update).
  const statusSelectLocked = isWithdrawnRow || isUnregisteredRow;
  const noteDisabled = form.status === 'ACTIVE';

  // 탈퇴 is terminal (customer PRD 21 §3.4), so a withdrawn row is read-only in full — not merely
  // unsavable. The Save button was already blocked for it, but leaving the inputs live let someone type
  // into a form that can never be submitted, and the server now refuses such an update outright
  // (INVALID_TARGET_STATUS), so an editable field here could only ever end in a rejected request.
  // Every field consults this instead of `canWrite` alone, so the two reasons a field locks — no write
  // permission, or a terminal row — stay in one expression rather than being repeated per input.
  const fieldsDisabled = !canWrite || isWithdrawnRow;

  // The email and phone shown for a withdrawn row are the values archived at withdrawal time; the account
  // released them so the addresses could be used again. `identityArchived` is what the server says about the
  // two values it just sent, which is narrower than "this row is withdrawn" — a withdrawn row whose archive
  // row is somehow missing sends no substituted values, and chipping empty fields would state something
  // untrue. Falling back to the status keeps the chip working against a server that predates the flag.
  const showArchivedChip = editing?.identityArchived ?? isWithdrawnRow;

  // [Reset Password] shows only for Active, non-Root admins (PRD 15 §3.6). Root accounts
  // are managed via transfer; Pending/Inactive/Withdrawn have no resettable password.
  const showResetPassword = editing?.status === 'ACTIVE' && !editing.isRoot;
  const writeTooltip = !canWrite
    ? t('adminManagement.tooltips.noWritePermission')
    : undefined;

  // No confirmation modal (PRD 15 §3.6 / customer update 220526): navigate straight to the
  // email send page. `send=true` enables that page's Cancel/Send action bar.
  const handleResetPasswordClick = () => {
    if (!editing) return;
    const q = new URLSearchParams({
      send: 'true',
      id: String(editing.id),
      name: editing.name,
      email: editing.email,
    }).toString();
    navigate(`/admin-password-reset-email?${q}`);
  };

  // Sync form with the admin being opened. Fires only when editing.id changes,
  // so user edits inside the panel are not clobbered mid-session.
  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        phone: editing.phone ?? '',
        roles: [...editing.roles],
        status: editing.status,
        note: editing.note ?? '',
      });
      setErrors({});
      setIsDirty(false);
      setShowDiscard(false);
      setPendingDiscard(null);
      setShowWithdrawConfirm(false);
    }
  }, [editing?.id]);

  const update = <K extends FieldKey>(key: K, value: EditFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Status change side-effect: switching to Active auto-clears Note (also disabled).
  const handleStatusChange = (next: AdminStatus) => {
    setForm((f) => ({
      ...f,
      status: next,
      note: next === 'ACTIVE' ? '' : f.note,
    }));
    setIsDirty(true);
  };

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    const name = form.name.trim();
    if (name.length < 2 || name.length > 50) {
      next.name = t('adminManagement.fields.name.errorRequired');
    } else if (hasEmoji(name)) {
      // SHCT-860 — NAME class: 이모지 불허 ([공통] 00 §4.2).
      next.name = t('common:errors.emojiNotAllowed');
    }
    // 비활성 사유 is free text that is persisted and shown in the status history,
    // so §4.2's ban applies to it as well — it had no validation at all before.
    if (hasEmoji(form.note)) {
      next.note = t('common:errors.emojiNotAllowed');
    }
    const digits = normalizePhone(form.phone);
    if (!digits) {
      next.phone = t('adminManagement.fields.phone.errorRequired');
    } else if (!PHONE_REGEX.test(digits)) {
      next.phone = t('adminManagement.fields.phone.errorInvalid');
    }
    if (form.roles.length === 0) {
      next.roles = t('adminManagement.fields.roles.errorRequired');
    }
    setErrors(next);
    const valid = Object.keys(next).length === 0;
    // SHCT-694 — PRD 15 §8.1: 해당 필드로 포커스가 이동한다. Deferred a frame so the
    // inline messages this reads have been committed.
    if (!valid) focusFirstInvalidFieldAfterRender(() => formRef.current);
    return valid;
  };

  const closeAndReset = () => {
    closeStore();
  };

  const handleClose = () => {
    if (updateMutation.isPending || resendMutation.isPending) return;
    if (isDirty) {
      setPendingDiscard('close');
      setShowDiscard(true);
      return;
    }
    closeAndReset();
  };

  const handleDiscardConfirm = () => {
    const action = pendingDiscard;
    setShowDiscard(false);
    setPendingDiscard(null);
    if (action === 'withdraw') {
      // User chose to abandon their edits and proceed with Withdraw.
      setShowWithdrawConfirm(true);
    } else {
      closeAndReset();
    }
  };

  const handleDiscardCancel = () => {
    setShowDiscard(false);
    setPendingDiscard(null);
  };

  const handleApiError = (e: unknown): void => {
    if (e instanceof AdminApiError) {
      if (e.code === 'ROOT_NOT_EDITABLE') {
        toast.warning(e.message ?? t('adminManagement.toasts.rootNotEditable'));
        closeStore();
        return;
      }
      if (e.code === 'ROLE_IN_USE_BY_SELF') {
        // Backstop: the self row already disables Role editing, but if the request
        // still reaches BE, show a self-context message instead of the borrowed
        // Role-Management delete text the server returns.
        toast.error(t('adminManagement.toasts.selfRoleChangeForbidden'));
        return;
      }
      if (
        e.code === 'SELF_INACTIVATE_FORBIDDEN' ||
        e.code === 'SELF_WITHDRAW_FORBIDDEN' ||
        e.code === 'INVALID_TARGET_STATUS'
      ) {
        // These three carry meaningful, locale-aware server copy, so the helper keeps it
        // as the defaultValue behind an i18n key (repo rule 4) rather than discarding it.
        toast.error(getAdminErrorMessage(e, t, 'adminManagement.toasts.updateError'));
        return;
      }
      if (e.code === 'PHONE_DUPLICATE') {
        // Anti-enumeration: a phone already used by another admin shows the single shared generic
        // message (locale-aware), inline on the phone field. The DB unique-phone rule returns
        // PHONE_DUPLICATE without a field map, so map the code here.
        setErrors((prev) => ({ ...prev, phone: t('errors.phoneRejected', { ns: 'common' }) }));
        focusFirstInvalidFieldAfterRender(() => formRef.current);
        return;
      }
      if (e.fields) {
        // §8.1's focus rule does not care which side produced the message (SHCT-694).
        setErrors(e.fields as Partial<Record<FieldKey, string>>);
        focusFirstInvalidFieldAfterRender(() => formRef.current);
        return;
      }
      // PRD 15 §8.2 저장 실패 copy. Previously `e.message`, which put the server's raw
      // 5xx text on screen (SHCT-685).
      toast.error(getAdminErrorMessage(e, t, 'adminManagement.toasts.updateError'));
      return;
    }
    toast.error(t('adminManagement.toasts.updateError'));
  };

  const handleSubmit = async () => {
    if (!editing || updateMutation.isPending) return;
    if (!validate()) return;
    const prevStatus = editing.status;
    const nextStatus = form.status;
    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        input: {
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          roles: form.roles,
          status: form.status,
          // Always send the current note. Server handles clear + history append
          // on Inactive → Active transition.
          note: form.note.trim() || null,
        },
      });
      closeStore();
      if (nextStatus === 'INACTIVE' && prevStatus !== 'INACTIVE') {
        toast.success(
          t('adminManagement.toasts.statusChangedToInactive', { name: form.name.trim() }),
        );
      } else if (nextStatus === 'ACTIVE' && prevStatus === 'INACTIVE') {
        toast.success(
          t('adminManagement.toasts.statusChangedToActive', { name: form.name.trim() }),
        );
      } else {
        toast.success(t('adminManagement.toasts.updateSuccess'));
      }
    } catch (e) {
      handleApiError(e);
    }
  };

  // Withdraw button → if dirty, prompt discard first (per Q6 answer 6).
  const handleWithdrawClick = () => {
    if (updateMutation.isPending || resendMutation.isPending) return;
    if (isDirty) {
      setPendingDiscard('withdraw');
      setShowDiscard(true);
      return;
    }
    setShowWithdrawConfirm(true);
  };

  const handleWithdrawConfirm = async () => {
    if (!editing || updateMutation.isPending) return;
    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        input: {
          status: 'WITHDRAWN',
          // Send the typed note as the final reason (Q6 option c). If the user
          // had discarded their edits via the prior modal, form.note is the
          // current editing.note (sync effect re-ran), so this is still safe.
          note: form.note.trim() || null,
        },
      });
      const targetName = editing.name;
      setShowWithdrawConfirm(false);
      closeStore();
      toast.success(
        t('adminManagement.toasts.statusChangedToWithdrawn', { name: targetName }),
      );
    } catch (e) {
      setShowWithdrawConfirm(false);
      handleApiError(e);
    }
  };

  // [재발송] — show the pre-send confirmation modal first (resend variant),
  // populated with the admin's stored values.
  const handleResendPreview = () => {
    if (!editing || resendMutation.isPending) return;
    setResendSentAt(formatSentAt(new Date()));
    setResendPreviewOpen(true);
  };

  const handleResendClick = async () => {
    if (!editing || resendMutation.isPending) return;
    try {
      await resendMutation.mutateAsync(editing.id);
      setResendPreviewOpen(false);
      toast.success(t('adminManagement.toasts.inviteSuccess'), {
        action: {
          label: t('adminManagement.toasts.invitePreviewAction'),
          // The real BE-rendered preview page already exists — open it in resend
          // mode instead of the legacy "unavailable" stub.
          onClick: () => navigate('/admin-invite-email?resend=true'),
        },
      });
      closeStore();
    } catch (e) {
      setResendPreviewOpen(false);
      toast.error(getAdminErrorMessage(e, t, 'adminManagement.toasts.resendError'));
    }
  };

  return (
    <>
      <SlidePanel
        open={editing != null}
        title={t('adminManagement.editPanel.title')}
        width={520}
        onClose={handleClose}
      >
        <div ref={formRef}>
        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.email.label')}
            <ArchivedIdentityChip show={showArchivedChip} />
          </label>
          <input
            type="email"
            className="form-field__input"
            value={editing?.email ?? ''}
            readOnly
            style={{ background: 'var(--muted)', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.fields.name.placeholder')}
            value={form.name}
            disabled={fieldsDisabled}
            {...denyProps}
            onChange={(e) => update('name', e.target.value)}
          />
          {errors.name && <p className="form-field__error">{errors.name}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.phone.label')}{' '}
            <span className="required">*</span>
            <ArchivedIdentityChip show={showArchivedChip} />
          </label>
          <input
            type="tel"
            className={`form-field__input${errors.phone ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.fields.phone.placeholder')}
            inputMode="numeric"
            value={form.phone}
            disabled={fieldsDisabled}
            {...denyProps}
            onChange={(e) => update('phone', toPhoneInputValue(e.target.value))}
          />
          {errors.phone && <p className="form-field__error">{errors.phone}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.roles.label')}{' '}
            <span className="required">*</span>
          </label>
          <RolePicker
            mode="single"
            options={assignableRoles}
            value={form.roles}
            onChange={(roles) => update('roles', roles)}
            idPrefix="edit-role"
            helperText={t('adminManagement.invitePanel.helperRoles')}
            multiRoleWarning={t('adminManagement.fields.roles.legacyMultiRole')}
            disabled={fieldsDisabled || updateMutation.isPending || isSelfRow}
            {...denyProps}
          />
          {isWithdrawnRow ? (
            <p className="form-field__help" style={helpStyle}>
              {t('adminManagement.editPanel.withdrawnFieldsLockNote')}
            </p>
          ) : isSelfRow ? (
            <p className="form-field__help" style={helpStyle}>
              {t('adminManagement.editPanel.selfRowRoleLockNote')}
            </p>
          ) : null}
          {errors.roles && <p className="form-field__error">{errors.roles}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.status.label')}
          </label>
          <StatusSelect
            value={form.status}
            currentStatus={editing?.status}
            isSelfRow={isSelfRow}
            disabled={!canWrite || updateMutation.isPending || statusSelectLocked}
            {...denyProps}
            onChange={handleStatusChange}
          />
          {isWithdrawnRow ? (
            <p className="form-field__help" style={helpStyle}>
              {t('adminManagement.editPanel.withdrawnLockNote')}
            </p>
          ) : isSelfRow ? (
            <p className="form-field__help" style={helpStyle}>
              {t('adminManagement.editPanel.selfRowInactiveLockNote')}
            </p>
          ) : null}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('adminManagement.fields.note.label')}
          </label>
          <input
            type="text"
            className={`form-field__input${errors.note ? ' form-field__input--error' : ''}`}
            placeholder={t('adminManagement.editPanel.notePlaceholder')}
            value={form.note}
            disabled={fieldsDisabled || noteDisabled || updateMutation.isPending}
            {...denyProps}
            onChange={(e) => update('note', e.target.value)}
          />
          {errors.note && <p className="form-field__error">{errors.note}</p>}
          <p className="form-field__help" style={helpStyle}>
            {t('adminManagement.editPanel.noteHelp')}
          </p>
        </div>

        <InactiveHistoryList history={editing?.inactiveHistory ?? []} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-6)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {isUnregisteredRow && (
              <button
                type="button"
                className="btn btn-default btn-primary"
                onClick={handleResendPreview}
                disabled={!canWrite || resendMutation.isPending || updateMutation.isPending}
                {...denyProps}
                title={writeTooltip}
              >
                {t('adminManagement.actions.resend')}
              </button>
            )}
            {showResetPassword && (
              <button
                type="button"
                className="btn btn-default btn-outline"
                style={{
                  color: canWrite ? 'var(--destructive)' : undefined,
                  borderColor: canWrite
                    ? 'var(--destructive-light, var(--destructive))'
                    : undefined,
                }}
                disabled={!canWrite || updateMutation.isPending || resendMutation.isPending}
                {...denyProps}
                title={writeTooltip}
                onClick={handleResetPasswordClick}
              >
                {t('adminManagement.actions.resetPassword')}
              </button>
            )}
            {editing != null && !isWithdrawnRow && !isSelfRow && (
              <button
                type="button"
                className="btn btn-default btn-outline"
                style={{
                  color: 'var(--destructive)',
                  borderColor: 'var(--destructive-light, var(--destructive))',
                }}
                onClick={handleWithdrawClick}
                disabled={!canWrite || updateMutation.isPending || resendMutation.isPending}
                {...denyProps}
                title={writeTooltip}
              >
                {t('adminManagement.actions.withdraw')}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleClose}
              disabled={updateMutation.isPending}
            >
              {t('adminManagement.editPanel.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handleSubmit}
              disabled={!canWrite || updateMutation.isPending || isWithdrawnRow}
              {...denyProps}
              title={
                !canWrite
                  ? writeTooltip
                  : isWithdrawnRow
                    ? t('adminManagement.editPanel.withdrawnLockNote')
                    : undefined
              }
            >
              {updateMutation.isPending
                ? t('adminManagement.editPanel.submitting')
                : t('adminManagement.editPanel.submit')}
            </button>
          </div>
        </div>
        </div>
      </SlidePanel>

      <ConfirmModal
        open={showDiscard}
        title={t('adminManagement.invitePanel.discardTitle')}
        message={t('adminManagement.invitePanel.discardMessage')}
        confirmLabel={t('adminManagement.modals.discard.confirm')}
        cancelLabel={t('adminManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
      />

      <ConfirmModal
        open={showWithdrawConfirm}
        title={t('adminManagement.withdrawConfirm.title')}
        message={t('adminManagement.withdrawConfirm.message', {
          name: editing?.name ?? '',
        })}
        confirmLabel={t('adminManagement.withdrawConfirm.confirm')}
        cancelLabel={t('adminManagement.withdrawConfirm.cancel')}
        confirmVariant="destructive"
        loading={updateMutation.isPending}
        loadingLabel={t('adminManagement.editPanel.submitting')}
        onConfirm={handleWithdrawConfirm}
        onCancel={() => setShowWithdrawConfirm(false)}
      />

      <AdminResendPreviewModal
        open={resendPreviewOpen}
        name={editing?.name ?? ''}
        email={editing?.email ?? ''}
        rolesLabel={(editing?.roles ?? []).join(', ')}
        sentAt={resendSentAt}
        submitting={resendMutation.isPending}
        onEdit={() => setResendPreviewOpen(false)}
        onConfirm={handleResendClick}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Local sub-components
// ---------------------------------------------------------------------------

const helpStyle: React.CSSProperties = {
  marginTop: 'var(--space-2)',
  color: 'var(--muted-foreground)',
  fontSize: 12,
};

// Marks an email/phone field whose value came out of the withdrawal archive rather than off the live row.
// It sits beside the field rather than inside the value so the address stays clean to read and copy — the
// operator's whole reason for wanting it back is to trace or paste it somewhere, and a value with a word
// appended is a value that no longer matches what they search for.
function ArchivedIdentityChip({ show }: { show: boolean }) {
  const { t } = useTranslation('admin');
  if (!show) return null;
  return (
    <span
      className="badge badge-secondary"
      style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, verticalAlign: 'middle' }}
      title={t('adminManagement.editPanel.archivedIdentityChipTooltip')}
    >
      {t('adminManagement.editPanel.archivedIdentityChip')}
    </span>
  );
}

interface StatusSelectProps {
  value: AdminStatus;
  currentStatus?: AdminStatus;
  isSelfRow: boolean;
  disabled: boolean;
  onChange: (next: AdminStatus) => void;
}

// PRD update 2026-05-26:
// - Active: always available.
// - Inactive: available only when not self-row AND current is Active/Inactive.
// - Unregistered / Withdrawn: present only when current row IS that status
//   (display-only — the select itself is also disabled in those cases).
function StatusSelect({
  value,
  currentStatus,
  isSelfRow,
  disabled,
  onChange,
  ...rest
}: StatusSelectProps & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'>) {
  const options: AdminStatus[] = ['ACTIVE'];
  if (!isSelfRow) options.push('INACTIVE');
  if (currentStatus === 'PENDING') options.push('PENDING');
  if (currentStatus === 'WITHDRAWN') options.push('WITHDRAWN');

  return (
    <StatusSelectInner
      value={value}
      options={options}
      disabled={disabled}
      onChange={onChange}
      {...rest}
    />
  );
}

function StatusSelectInner({
  value,
  options,
  disabled,
  onChange,
  ...rest
}: {
  value: AdminStatus;
  options: AdminStatus[];
  disabled: boolean;
  onChange: (next: AdminStatus) => void;
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'>) {
  const { t } = useTranslation('admin');
  return (
    <select
      className="form-field__select"
      {...rest}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as AdminStatus)}
    >
      {options.map((s) => (
        <option key={s} value={s}>
          {t(`adminStatus.${s}`, { ns: 'labels' })}
        </option>
      ))}
    </select>
  );
}

function InactiveHistoryList({
  history,
}: {
  history: NonNullable<Admin['inactiveHistory']>;
}) {
  const { t } = useTranslation('admin');
  if (!history || history.length === 0) return null;
  const entries = [...history].reverse();
  return (
    <div className="form-field">
      <label className="form-field__label">
        {t('adminManagement.editPanel.inactiveHistoryLabel')}
      </label>
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '8px 12px',
          background: 'var(--muted)',
          fontSize: 13,
        }}
      >
        {entries.map((h, idx) => (
          <div
            key={`${h.from}-${h.to}-${idx}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '3px 0',
            }}
          >
            <span>
              ·{' '}
              {h.reason?.trim() ||
                t('adminManagement.editPanel.inactiveHistoryReasonFallback')}
            </span>
            <span
              style={{
                color: 'var(--muted-foreground)',
                whiteSpace: 'nowrap',
              }}
            >
              {formatHistoryDate(h.from)} ~ {formatHistoryDate(h.to)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// History dates come ISO from the wire; formatLastLogin gives `YYYY.MM.DD HH:MM`.
// We trim to `YYYY.MM.DD` for the compact history view (matches prototype).
function formatHistoryDate(value: string): string {
  if (!value) return '-';
  const fmt = formatLastLogin(value);
  if (fmt === '-') return value;
  return fmt.slice(0, 10);
}
